import { useEffect, useState } from "react";
import { useBookingsStore } from "../../context/useBookingsStore";
import { useVehiclesStore } from "../../context/useVehiclesStore";
import { useUserAuthStore } from "../../context/useUserAuthStore";

export default function BookingCreateModal({
  onClose,
  onDone,
  presetVehicle, // optional (for "Book this vehicle")
}) {
  const { currentUser } = useUserAuthStore();
  const { createBooking } = useBookingsStore();
  const { vehicles, fetchVehicles } = useVehiclesStore();

  const isAdmin = currentUser?.role === "admin";

  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    vehicleId: presetVehicle?.id || "",
    plannedStartTime: "",
    plannedEndTime: "",
    reason: "",
  });

  useEffect(() => {
    if (!vehicles.length) fetchVehicles();
  }, [vehicles.length, fetchVehicles]);

  // Driver can only book vehicles linked to them
  const availableVehicles = vehicles;

  async function submit() {
    setError(null);

    if (
      !form.vehicleId ||
      !form.plannedStartTime ||
      !form.plannedEndTime ||
      !form.reason.trim()
    ) {
      setError("All fields are required");
      return;
    }

    if (
      new Date(form.plannedEndTime) <=
      new Date(form.plannedStartTime)
    ) {
      setError("End time must be after start time");
      return;
    }

    try {
      setSubmitting(true);
      await createBooking({
        ...form,
        reason: form.reason.trim(),
      });
      onDone();
    } catch (e) {
      setError(e.message || "Failed to create booking");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal">
      <h3 className="text-lg font-semibold mb-2">
        Request Booking
      </h3>

      {error && (
        <div className="text-red-600 text-sm mb-2">
          {error}
        </div>
      )}

      {/* Vehicle */}
      <label className="block text-sm mb-1">Vehicle</label>
      <select
        value={form.vehicleId}
        disabled={!!presetVehicle}
        onChange={(e) =>
          setForm({ ...form, vehicleId: e.target.value })
        }
      >
        <option value="">Select vehicle</option>
        {availableVehicles.map((v) => (
          <option key={v.id} value={v.id}>
            {v.registrationNumber || v.make || `Vehicle #${v.id}`}
          </option>
        ))}
      </select>

      {/* Start */}
      <label className="block text-sm mt-2 mb-1">
        Planned start
      </label>
      <input
        type="datetime-local"
        value={form.plannedStartTime}
        onChange={(e) =>
          setForm({
            ...form,
            plannedStartTime: e.target.value,
          })
        }
      />

      {/* End */}
      <label className="block text-sm mt-2 mb-1">
        Planned end
      </label>
      <input
        type="datetime-local"
        value={form.plannedEndTime}
        onChange={(e) =>
          setForm({
            ...form,
            plannedEndTime: e.target.value,
          })
        }
      />

      {/* Reason */}
      <label className="block text-sm mt-2 mb-1">
        Reason for booking
      </label>
      <textarea
        rows={3}
        placeholder="e.g. Client visit, maintenance, site inspection"
        value={form.reason}
        onChange={(e) =>
          setForm({ ...form, reason: e.target.value })
        }
      />

      <div className="mt-3 flex gap-2">
        <button onClick={submit} disabled={submitting}>
          {submitting ? "Submitting..." : "Request"}
        </button>
        <button className="secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}
